/**
 * useDemoOrgProvisionStream
 *
 * Subscribes to live demo-org provisioning progress over SSE
 * (`GET /v2/admin/demo-org/jobs/{jobId}/stream`) and exposes a small state surface
 * for a phase stepper. Mirrors the app's streaming pattern (fetch-event-source +
 * Auth0 bearer header; see note-ai-edit/useNoteGenerationStreaming).
 *
 * Resilience: the Celery result backend stays authoritative. If the stream is
 * missed, dropped, or times out before a terminal event, the hook reconciles once
 * against `GET /v2/admin/demo-org/jobs/{jobId}` so the UI never spins forever.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Generous cap; provisioning ~8 personas is tens of seconds. The server relay has
// its own 120s idle timeout, after which we reconcile authoritative state.
const STREAM_TIMEOUT_MS = 3 * 60 * 1000;

export interface ProvisionPhase {
  id: string;
  label: string;
}

/** Fixed phase manifest — also the fallback if the RUN_STARTED event is missed. */
export const PROVISION_PHASES: ProvisionPhase[] = [
  { id: 'validating', label: 'Validating roster' },
  { id: 'org_owner', label: 'Creating organization & owner' },
  { id: 'personas', label: 'Provisioning personas' },
  { id: 'cosign', label: 'Wiring co-signatures' },
  { id: 'finalizing', label: 'Finalizing' },
];

const PHASE_ORDER = PROVISION_PHASES.map((p) => p.id);

export type ProvisionStreamStatus =
  | 'idle'
  | 'connecting'
  | 'streaming'
  | 'completed'
  | 'failed';

export interface ProvisionStreamResult {
  organization_id?: string;
  provider_mapping?: Record<string, string>;
}

export interface UseDemoOrgProvisionStreamResult {
  status: ProvisionStreamStatus;
  currentPhase: string | null;
  completedPhases: string[];
  detail: string | null;
  result: ProvisionStreamResult | null;
  error: string | null;
}

interface StreamEvent {
  type: string;
  phase?: string;
  message?: string;
  index?: number;
  total?: number;
  organization_id?: string;
  provider_mapping?: Record<string, string>;
  error?: string;
  status?: string;
}

export function useDemoOrgProvisionStream(
  jobId: string | null,
): UseDemoOrgProvisionStreamResult {
  const [status, setStatus] = useState<ProvisionStreamStatus>('idle');
  const [currentPhase, setCurrentPhase] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [result, setResult] = useState<ProvisionStreamResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const terminalRef = useRef(false);

  useEffect(() => {
    if (!jobId) {
      setStatus('idle');
      setCurrentPhase(null);
      setDetail(null);
      setResult(null);
      setError(null);
      terminalRef.current = false;
      return;
    }

    terminalRef.current = false;
    setStatus('connecting');
    setCurrentPhase('validating');
    setDetail(null);
    setResult(null);
    setError(null);

    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const finishCompleted = (res: ProvisionStreamResult) => {
      if (terminalRef.current) return;
      terminalRef.current = true;
      setResult(res);
      setStatus('completed');
      controller.abort();
    };

    const finishFailed = (msg: string) => {
      if (terminalRef.current) return;
      terminalRef.current = true;
      setError(msg);
      setStatus('failed');
      controller.abort();
    };

    // One-shot reconcile against the authoritative result backend.
    const reconcile = async (token: string) => {
      if (terminalRef.current) return;
      try {
        const res = await fetch(
          `${API_BASE_URL}/v2/admin/demo-org/jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok) {
          const job = (await res.json()) as StreamEvent;
          if (job.status === 'completed') {
            finishCompleted({
              organization_id: job.organization_id,
              provider_mapping: job.provider_mapping,
            });
            return;
          }
          if (job.status === 'failed') {
            finishFailed(job.error || 'Provisioning failed');
            return;
          }
        }
      } catch {
        // fall through to the soft error below
      }
      finishFailed(
        'Lost connection to the progress stream. The job may still be running — reload to check its status.',
      );
    };

    const run = async () => {
      let token: string;
      try {
        token = await getAuth0AccessTokenSilently();
      } catch {
        finishFailed('Could not authenticate the progress stream.');
        return;
      }

      timeoutId = setTimeout(() => {
        if (!terminalRef.current) reconcile(token);
      }, STREAM_TIMEOUT_MS);

      try {
        await fetchEventSource(
          `${API_BASE_URL}/v2/admin/demo-org/jobs/${jobId}/stream`,
          {
            method: 'GET',
            signal: controller.signal,
            // Keep streaming if the operator switches tabs mid-provision.
            openWhenHidden: true,
            headers: { Authorization: `Bearer ${token}` },
            onopen: async (response) => {
              if (!response.ok) {
                throw new Error(`Stream failed: ${response.status}`);
              }
              if (!terminalRef.current) setStatus('streaming');
            },
            onmessage: (ev) => {
              if (!ev.data) return;
              let data: StreamEvent;
              try {
                data = JSON.parse(ev.data) as StreamEvent;
              } catch {
                return;
              }
              switch (data.type) {
                case 'progress':
                  if (data.phase) setCurrentPhase(data.phase);
                  if (data.message) setDetail(data.message);
                  break;
                case 'completed':
                  finishCompleted({
                    organization_id: data.organization_id,
                    provider_mapping: data.provider_mapping,
                  });
                  break;
                case 'failed':
                  finishFailed(data.error || 'Provisioning failed');
                  break;
                case 'error':
                  // Server-side relay timeout/error — reconcile authoritative state.
                  reconcile(token);
                  break;
                default:
                  // 'completion' sentinel and anything else: ignore.
                  break;
              }
            },
            onerror: (err) => {
              if (controller.signal.aborted) return;
              // Stop fetch-event-source's auto-retry; the outer catch reconciles.
              throw err;
            },
            onclose: () => {
              if (!terminalRef.current) reconcile(token);
            },
          },
        );
      } catch {
        if (controller.signal.aborted) return;
        if (!terminalRef.current) reconcile(token);
      }
    };

    run();

    return () => {
      controller.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [jobId]);

  const currentIdx = currentPhase ? PHASE_ORDER.indexOf(currentPhase) : -1;
  const completedPhases =
    status === 'completed'
      ? PHASE_ORDER
      : currentIdx > 0
        ? PHASE_ORDER.slice(0, currentIdx)
        : [];

  return { status, currentPhase, completedPhases, detail, result, error };
}
